import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ticketId", "threadId", "uploadedFiles"];
import { defineMutation } from 'pulse/mutations';
import { useMutation } from 'pulse/react';
import { toSuccess } from 'pulse/resolvers';
import { useCallback, useEffect, useState } from 'react';
import { generatePath, useHistory } from 'quartz-routing/react-router';
import { convertToHtml, convertToPlaintext } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { TicketThread, useTicketThread } from './useTicketThread';
import { GetThreadAttachmentsRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/AttachmentsRpc.resources';
import { MESSAGE_CHAR_LIMIT, UnpublishableReasonEnum } from '../constants';
import { FileUploadStatus } from './types';
import { ROUTES } from '../constants/routes';
import { publishMessageRpc, publishMessageToNewThreadRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/MessagePublishingRpc';
import { chirpClient } from '../utils/chirpClient';
import { getPortalAccountId, getPortalInstanceId } from 'portals-platform-lib';
const toCommonMessage = msg => Object.assign({}, msg, {
  '@type': 'COMMON_MESSAGE'
});
const publishMessage = _ref => {
  let {
      ticketId,
      threadId
    } = _ref,
    payload = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (threadId != null) {
    return chirpClient.call(publishMessageRpc, {
      request: {
        accountId: getPortalAccountId(),
        portalInstanceId: getPortalInstanceId(),
        params: Object.assign({
          ticketId,
          threadId
        }, payload)
      }
    });
  }
  return chirpClient.call(publishMessageToNewThreadRpc, {
    request: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      params: Object.assign({
        ticketId
      }, payload)
    }
  });
};
const toFileAttachment = file => ({
  expiresAt: file.expiresAt,
  url: file.downloadUrl,
  name: file.fileName,
  extension: file.fileExtension,
  type: file.fileType,
  size: file.fileSize,
  height: file.imageHeight,
  width: file.imageWidth
});
export const PublishMessage = defineMutation({
  typeName: 'customerPortalApp:PublishMessage',
  mutation: publishMessage,
  resolved: ({
    params,
    result,
    setState
  }) => {
    const {
      ticketId,
      threadId,
      uploadedFiles
    } = params;
    if (!threadId) {
      return;
    }
    const {
      message,
      responder
    } = result;
    setState({
      resource: TicketThread,
      params: {
        ticketId,
        threadId
      },
      state: current => {
        const cachedThread = current === null || current === void 0 ? void 0 : current.data;
        if (!cachedThread) {
          return current;
        }
        const updatedAttachments = Object.assign({}, cachedThread.attachments);
        uploadedFiles.forEach(file => {
          if (!cachedThread.attachments[file.id]) {
            updatedAttachments[file.id] = {
              attachmentTimestamp: Date.now(),
              fileMetadata: toFileAttachment(file)
            };
          }
        });
        const updatedResponders = Object.assign({}, cachedThread.responders);
        if (responder && message.sender) {
          updatedResponders[JSON.stringify(message.sender)] = responder;
        }
        return toSuccess(Object.assign({}, cachedThread, {
          responders: updatedResponders,
          attachments: updatedAttachments,
          results: [toCommonMessage(message), ...(cachedThread.results || [])]
        }))();
      }
    });
    setState({
      resource: GetThreadAttachmentsRpc,
      params: {
        params: {
          ticketId,
          threadId
        }
      },
      state: current => {
        const cachedAttachments = current === null || current === void 0 ? void 0 : current.data;
        if (!cachedAttachments || uploadedFiles.length === 0) {
          return current;
        }
        const existingAttachments = cachedAttachments.attachments;
        const latestAttachmentTimestamp = existingAttachments.reduce((latestTimestamp, attachment) => Math.max(latestTimestamp, attachment.attachmentTimestamp), 0);
        if (message.timestamp <= latestAttachmentTimestamp) {
          return current;
        }
        const newAttachments = uploadedFiles.map(file => ({
          attachmentTimestamp: message.timestamp,
          fileMetadata: toFileAttachment(file)
        }));
        return toSuccess(Object.assign({}, cachedAttachments, {
          attachments: [...newAttachments, ...existingAttachments]
        }))();
      }
    });
  }
});
export function usePublishMessage({
  ticketId,
  threadId,
  richEditorContent,
  uploads,
  clearEditor
}) {
  const history = useHistory();
  const {
    lastMessageId: inReplyToId
  } = useTicketThread(ticketId, threadId);
  const [mutate, {
    loading,
    error
  }] = useMutation(PublishMessage);
  const onCompleted = useCallback(() => {
    clearEditor();
    if (!threadId) {
      const params = new URLSearchParams(history.location.search);
      params.set('creatingThread', '1');
      history.replace({
        pathname: generatePath(ROUTES.ticket, {
          ticketId
        }),
        search: `?${params.toString()}`
      });
    }
  }, [clearEditor, history, threadId, ticketId]);
  const [isPublishable, setIsPublishable] = useState(false);
  const [unpublishableReason, setUnpublishableReason] = useState(UnpublishableReasonEnum.NO_CONTENT);
  const handlePublishMessage = useCallback(() => {
    if (!isPublishable || !richEditorContent) {
      return;
    }
    const htmlContent = convertToHtml(richEditorContent);
    const plaintextContent = convertToPlaintext(richEditorContent);
    const uploadedFiles = uploads.filter(upload => upload.status === FileUploadStatus.UPLOADED);
    const fileIds = uploadedFiles.map(upload => upload.id);
    const payload = Object.assign({
      ticketId,
      threadId,
      inReplyToId,
      fileIds,
      uploadedFiles,
      message: plaintextContent
    }, htmlContent && {
      richMessage: htmlContent
    });
    mutate(payload).then(onCompleted).catch(() => {});
  }, [inReplyToId, ticketId, threadId, isPublishable, mutate, onCompleted, richEditorContent, uploads]);
  useEffect(() => {
    const plainTextContent = convertToPlaintext(richEditorContent);
    const htmlContent = convertToHtml(richEditorContent);
    const hasMessage = plainTextContent.trim() !== '';
    const hasUploadedFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADED);
    const hasUploadingFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADING || upload.status === FileUploadStatus.PENDING);
    const hasFailedUploads = uploads.some(upload => upload.status === FileUploadStatus.ERROR);
    const validMessageLength = htmlContent.length <= MESSAGE_CHAR_LIMIT;
    let unpublishable = null;
    if (!(hasMessage || hasUploadedFiles)) {
      unpublishable = UnpublishableReasonEnum.NO_CONTENT;
    } else if (hasFailedUploads) {
      unpublishable = UnpublishableReasonEnum.HAS_FAILED_UPLOADS;
    } else if (hasUploadingFiles) {
      unpublishable = UnpublishableReasonEnum.UPLOADING;
    } else if (!validMessageLength) {
      unpublishable = UnpublishableReasonEnum.EXCEEDS_CHAR_LIMIT;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUnpublishableReason(unpublishable);
    setIsPublishable(unpublishable === null);
  }, [richEditorContent, uploads]);
  return {
    publishMessage: handlePublishMessage,
    publishable: {
      isPublishable,
      unpublishableReason
    },
    isPublishingMessage: loading,
    error
  };
}